import { KB_HIGHLIGHT_END, KB_HIGHLIGHT_START } from '@/api/kb-client';
import type { KbThreadSummaryDto } from '@/api/kb-client';
import type { ThreadSummaryDto } from '@/api/chat-client';
import type { SupplierDashboardDto } from '@/api/supplier-dashboard-client';

// =============================================================================
// echanges-model.ts — Sélecteurs PURS de fusion « Mes échanges avec JeKo » (GATE_HUMAN 2026-09-11).
// =============================================================================
// FUSION CÔTÉ CLIENT (aucune modification d'API/contrat) de DEUX read-models existants en UN modèle
// « conversation » unifié :
//   • Chat (EN COURS)   — ThreadSummaryDto   (source 'chat', isArchived=false, unread=unreadCount>0)
//   • KB   (ARCHIVÉES)  — KbThreadSummaryDto (source 'kb',   isArchived=true,  unread=false)
// Les DTO threads ne portent PAS le nom de campagne → JOINTURE avec le read-model dashboard fournisseur
// (active[]/past[] → map campaignId → intitulé) pour afficher/rechercher par nom. Modules PUR : aucune
// dépendance React/réseau, entièrement testable (fusion, tri, filtres date/archivé/non-lu, jointure noms).

/** Origine d'une conversation unifiée : chat 1-to-1 en cours, ou fil archivé (KB). */
export type ConversationSource = 'chat' | 'kb';

/**
 * Conversation UNIFIÉE (projection commune des deux read-models). `date` = dernier message (tri récence,
 * ISO 8601 ou `null` si le fil n'a aucun message). `campaignName` = intitulé joint au dashboard, ou `null`
 * si non résolu (affichage de repli sur l'identifiant). `preview` = aperçu texte brut (chat) ou extrait KB
 * (sentinelles de surlignage retirées — texte pur pour la liste).
 */
export interface UnifiedConversation {
  readonly threadId: string;
  readonly campaignId: string;
  readonly campaignName: string | null;
  readonly source: ConversationSource;
  readonly isArchived: boolean;
  /** ISO 8601 du dernier message (tri), ou `null`. */
  readonly date: string | null;
  /** `true` si des messages de la contrepartie sont non lus (chat uniquement ; KB toujours `false`). */
  readonly unread: boolean;
  readonly preview: string | null;
}

/**
 * Filtres de VUE (portés par l'URL côté route). `campaignId` = filtre par campagne (le nom sert à la
 * sélection, le filtre s'applique sur l'identifiant). `dateFrom`/`dateTo` = bornes de période (YYYY-MM-DD)
 * sur la date de conversation, SANS distinction en cours/archivé. `includeArchived` = inclut la source KB.
 * `unreadOnly` = ne garde que les non-lus.
 */
export interface EchangesFilters {
  readonly campaignId?: string;
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly includeArchived: boolean;
  readonly unreadOnly: boolean;
}

/** Une option de sélection de campagne (mode admin : sélecteur de campagne, pas un champ SIRET libre). */
export interface CampaignOption {
  readonly campaignId: string;
  readonly intitule: string;
}

/** Construit la table campaignId → intitulé à partir du dashboard (active[] ∪ past[]). */
export function buildCampaignNameMap(
  dashboard: SupplierDashboardDto | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (dashboard === undefined) {
    return map;
  }
  for (const p of [...dashboard.active, ...dashboard.past]) {
    if (!map.has(p.campaignId)) {
      map.set(p.campaignId, p.intitule);
    }
  }
  return map;
}

/** Options de campagne (dédoublonnées, triées par intitulé FR) pour le sélecteur de la recherche. */
export function campaignOptions(
  dashboard: SupplierDashboardDto | undefined,
): CampaignOption[] {
  const map = buildCampaignNameMap(dashboard);
  return [...map.entries()]
    .map(([campaignId, intitule]) => ({ campaignId, intitule }))
    .sort((a, b) => a.intitule.localeCompare(b.intitule, 'fr'));
}

/** Retire les sentinelles de surlignage KB (PUA) d'un extrait — texte pur pour la liste. */
function stripHighlights(snippet: string | null): string | null {
  if (snippet === null) {
    return null;
  }
  return snippet
    .split(KB_HIGHLIGHT_START)
    .join('')
    .split(KB_HIGHLIGHT_END)
    .join('');
}

/** Projette un fil de chat (EN COURS) en conversation unifiée. */
export function fromChatThread(
  thread: ThreadSummaryDto,
  nameByCampaign: Map<string, string>,
): UnifiedConversation {
  return {
    threadId: thread.threadId,
    campaignId: thread.campaignId,
    campaignName: nameByCampaign.get(thread.campaignId) ?? null,
    source: 'chat',
    isArchived: false,
    date: thread.lastMessageAt ?? thread.lastActivityAt,
    unread: thread.unreadCount > 0,
    preview: thread.lastMessagePreview,
  };
}

/** Projette un fil archivé (KB) en conversation unifiée. */
export function fromKbThread(
  thread: KbThreadSummaryDto,
  nameByCampaign: Map<string, string>,
): UnifiedConversation {
  return {
    threadId: thread.threadId,
    campaignId: thread.campaignId,
    campaignName: nameByCampaign.get(thread.campaignId) ?? null,
    source: 'kb',
    isArchived: true,
    date: thread.lastMessageAt ?? thread.archivedAt ?? thread.firstMessageAt,
    unread: false,
    preview: stripHighlights(thread.snippet),
  };
}

/** Comparateur de récence : date desc (ISO tri lexical), les `null` en dernier. */
function byDateDesc(a: UnifiedConversation, b: UnifiedConversation): number {
  const da = a.date ?? '';
  const db = b.date ?? '';
  if (da === db) {
    return 0;
  }
  return da < db ? 1 : -1;
}

/**
 * Fusionne les deux listes en un modèle unifié, DÉDOUBLONNÉ par threadId (un fil archivé n'apparaît que
 * via KB : la source KB PRIME en cas de collision), puis TRIÉ par date décroissante.
 */
export function mergeConversations(
  chatThreads: readonly ThreadSummaryDto[],
  kbThreads: readonly KbThreadSummaryDto[],
  nameByCampaign: Map<string, string>,
): UnifiedConversation[] {
  const byThread = new Map<string, UnifiedConversation>();
  for (const t of chatThreads) {
    byThread.set(t.threadId, fromChatThread(t, nameByCampaign));
  }
  // KB PRIME : écrase l'entrée chat de même threadId (le fil est consultable en archive, lecture seule).
  for (const t of kbThreads) {
    byThread.set(t.threadId, fromKbThread(t, nameByCampaign));
  }
  return [...byThread.values()].sort(byDateDesc);
}

/** Applique les filtres de vue (campagne, période, archivées, non-lues) à la liste fusionnée. */
export function applyFilters(
  conversations: readonly UnifiedConversation[],
  filters: EchangesFilters,
): UnifiedConversation[] {
  return conversations.filter((c) => {
    // Checkbox « Conversations Archivées » : exclut la source KB si non cochée.
    if (!filters.includeArchived && c.source === 'kb') {
      return false;
    }
    // Checkbox « Conversations non lues » : ne garde que les non-lus.
    if (filters.unreadOnly && !c.unread) {
      return false;
    }
    // Filtre campagne (par identifiant ; le nom sert seulement à la sélection).
    if (
      filters.campaignId !== undefined &&
      filters.campaignId.length > 0 &&
      c.campaignId !== filters.campaignId
    ) {
      return false;
    }
    // Bornes de période sur la date de conversation (comparaison sur la part calendaire YYYY-MM-DD).
    const day = c.date === null ? null : c.date.slice(0, 10);
    if (filters.dateFrom !== undefined && filters.dateFrom.length > 0) {
      if (day === null || day < filters.dateFrom) {
        return false;
      }
    }
    if (filters.dateTo !== undefined && filters.dateTo.length > 0) {
      if (day === null || day > filters.dateTo) {
        return false;
      }
    }
    return true;
  });
}
